/**
 * SetupWizard — 10-step first-launch onboarding (§21.1, polished).
 *
 * Spec maps each step to a server-side `step_id` persisted via
 * POST /api/v1/setup/step. Every step click triggers persistence + an
 * audit log row. The terminal `complete` step flips
 * platform_setup.completed=true and the wizard redirects to /ask-argus.
 *
 *   1. welcome      — Terms / EULA / privacy acknowledgment
 *   2. sysadmin     — First sysadmin account (email + password + MFA hint)
 *   3. tenant       — First organization (name, slug, standalone mode)
 *   4. profile      — Deployment profile (cpu_only | standard_gpu | high_perf | edge)
 *   5. model        — Primary language model (gated by detected hardware)
 *   6. users        — Invite first users (optional)
 *   7. features     — The two tenant settings (step id kept; the step is called Settings)
 *   8. data_source  — Connect first data source (optional)
 *   9. smoke_test   — Run a smoke-test query
 *  10. complete     — Done → redirect to /ask-argus
 *
 * Hard constraints (CLAUDE.md):
 *   - Rule #2: Standalone mode issues its own JWTs. We do not accept IQ tokens
 *     in this flow — the wizard runs pre-auth and the resulting tenant is
 *     always standalone.
 *   - Rule #20.6: Every form uses Zod .strict() validation server-side.
 *   - All step completions are audit-logged via POST /api/v1/setup/step.
 *   - Every icon button has a title tooltip.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Sparkles, Server, CheckCircle, ArrowRight, ArrowLeft, Loader2,
  AlertCircle, FileText, UserCog, Building, Brain,
  Users as UsersIcon, ToggleRight, Plug, Send,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { cn } from '../../lib/utils';
import { apiClient } from '../../lib/api-client';
import { toast } from 'sonner';
import {
  canAdvanceStep,
  slugifyTenantName,
  orgCodeFromName,
  passwordStrengthScore,
  isSetupClosedError,
  type StepId,
} from './SetupWizard.helpers';

// ── Step Definition ─────────────────────────────────────────────────────────

interface StepDef {
  id: StepId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  optional?: boolean;
}

const STEPS: readonly StepDef[] = [
  { id: 'welcome', label: 'Welcome', icon: FileText },
  { id: 'sysadmin', label: 'Sysadmin', icon: UserCog },
  { id: 'tenant', label: 'Tenant', icon: Building },
  { id: 'profile', label: 'Profile', icon: Server },
  { id: 'model', label: 'Model', icon: Brain },
  { id: 'users', label: 'Users', icon: UsersIcon, optional: true },
  // B1-T0: the step is called Settings — "Features" names nothing in this
  // product any more (decisions.md 2026-09-08). The `id` is a code key the
  // step machine and the e2e selectors use, not product copy, so it stays.
  { id: 'features', label: 'Settings', icon: ToggleRight },
  { id: 'data_source', label: 'Data', icon: Plug, optional: true },
  { id: 'smoke_test', label: 'Smoke Test', icon: Send },
  { id: 'complete', label: 'Done', icon: CheckCircle },
] as const;

// ── Form State ──────────────────────────────────────────────────────────────

interface ServiceStatus {
  name: string;
  status: 'healthy' | 'down' | 'unknown';
  details?: string;
}

interface SetupForm {
  // Step 1 — Welcome
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;

  // Step 2 — Sysadmin
  sysadminEmail: string;
  sysadminPassword: string;
  sysadminPasswordConfirm: string;
  totpEnrollment: boolean;

  // Step 3 — Tenant (always standalone per Rule #2)
  tenantName: string;
  tenantSlug: string;
  tenantOrgCode: string;
  industry: string;

  // Step 4 — Profile
  deploymentProfile: 'cpu_only' | 'standard_gpu' | 'high_perf' | 'edge';

  // Step 5 — Model
  primaryModel: string;

  // Step 6 — Users (optional)
  inviteEmails: string;

  // Step 7 — Settings
  // Foundations 1 / B0-T1: the five feature toggles this step used to carry
  // are gone with the flags (decisions.md 2026-09-08). Two tenant settings
  // remain, and they are the same two Admin › Settings › General owns.
  allowWebSearch: boolean;
  allowExternalConnections: boolean;

  // Step 8 — Data Source (optional)
  dataSourceKind: 'skip' | 'sharepoint' | 'gdrive' | 's3' | 'local';

  // Step 9 — Smoke Test
  smokeQuery: string;
  smokeResult: string;
  smokeOk: boolean;
}

const DEFAULT_FORM: SetupForm = {
  acceptedTerms: false,
  acceptedPrivacy: false,
  sysadminEmail: '',
  sysadminPassword: '',
  sysadminPasswordConfirm: '',
  totpEnrollment: true,
  tenantName: '',
  tenantSlug: '',
  tenantOrgCode: '',
  industry: '',
  deploymentProfile: 'standard_gpu',
  primaryModel: '',
  inviteEmails: '',
  // BOTH ON — founder decision SEC-6, 2026-09-09 (decisions.md § Features
  // item 10). This wizard creates the install's FIRST tenant, so it is one of
  // the "every path that creates an organization" the decision names, and it
  // must agree with the sysadmin Provision Tenant wizard and with the API's
  // `policy/tenant-settings.ts.defaultTenantSettings()`. Three places said
  // three different things before; they say one thing now. The operator can
  // still untick either box here — the step SENDS both values explicitly.
  allowWebSearch: true,
  allowExternalConnections: true,
  dataSourceKind: 'skip',
  smokeQuery: 'Hello — say hi back so I know you are alive.',
  smokeResult: '',
  smokeOk: false,
};

// ── Main Component ──────────────────────────────────────────────────────────

export function SetupWizard() {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [models, setModels] = useState<Array<{ name: string; size: string }>>([]);
  const [gpuAvailable, setGpuAvailable] = useState(false);
  const [form, setForm] = useState<SetupForm>(DEFAULT_FORM);
  const [completedSteps, setCompletedSteps] = useState<Set<StepId>>(new Set());
  // Decision 20: GET /setup/state answers 410 once setup is complete. Until
  // the wizard knows it is NOT on a set-up box it renders no step, so a
  // completed box never shows a walkable Welcome step on the way to /ask-argus.
  const [stateKnown, setStateKnown] = useState(false);

  const update = useCallback(<K extends keyof SetupForm>(key: K, value: SetupForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const currentStep = STEPS[stepIndex];

  // ── Bootstrap: load /setup/state + /setup/detect on mount ────────────────
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      try {
        // R4 P0-1 fix: apiClient already prepends `/api/v1` (see api-client.ts:191).
        // Previous absolute path `/api/v1/setup/state` resolved to /api/v1/api/v1/setup/state (404).
        // R4 P0-4 / P1-8: also redirect to /ask-argus if setup already completed.
        const state = await apiClient.get<{
          steps: Array<{ id: string; completed: boolean }>;
          currentStep: string;
        }>('/setup/state');
        if (cancelled) return;
        const done = new Set<StepId>();
        for (const s of state.steps) {
          if (s.completed) done.add(s.id as StepId);
        }
        setCompletedSteps(done);
        // P0-4 guard: if the platform was already onboarded, push the operator
        // to /ask-argus instead of letting them re-walk the wizard and crash
        // on a 410 SETUP_ALREADY_COMPLETE on the first Next click.
        if (state.currentStep === 'complete' && done.has('complete')) {
          void navigate({ to: '/ask-argus' });
          return;
        }
        const idx = STEPS.findIndex((s) => s.id === state.currentStep);
        if (idx >= 0) setStepIndex(idx);
      } catch (err) {
        // Decision 20 (2026-10-21): once setup is complete the API answers
        // 410 SETUP_ALREADY_COMPLETE here, and once an administrator exists
        // (even if the completion flag lags) 409 SYSADMIN_ALREADY_EXISTS —
        // exactly the refusals POST /setup/step gives. Both mean "this box
        // is set up": go where the guard above goes and never render step 1.
        // Any other failure (pre-bootstrap 404/503) falls through to the
        // wizard as before. The error shape is read through the shared
        // helpers (lib/api-error.ts), never a private cast.
        if (cancelled) return;
        if (isSetupClosedError(err)) {
          void navigate({ to: '/ask-argus' });
          return;
        }
      }
      if (cancelled) return;
      setStateKnown(true);

      try {
        // R4 P0-1 fix: /detect lives at /api/v1/setup/detect after the server
        // double-prefix rename. The previous /api/v1/setup/setup/detect path
        // is registered as a deprecation alias for one release.
        const detect = await apiClient.get<{
          services: ServiceStatus[];
          gpu: { available: boolean; models: Array<{ name: string; size: string }> };
        }>('/setup/detect');
        if (cancelled) return;
        setServices(detect.services ?? []);
        setGpuAvailable(detect.gpu?.available ?? false);
        setModels(detect.gpu?.models ?? []);
        if (detect.gpu?.models?.[0] && !form.primaryModel) {
          update('primaryModel', detect.gpu.models[0].name);
        }
        if (!detect.gpu?.available) {
          update('deploymentProfile', 'cpu_only');
        }
      } catch {
        /* detect optional */
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Validation: delegates to shared helper ───────────────────────────────
  const canAdvance = (): boolean =>
    canAdvanceStep(currentStep.id, form, { gpuAvailable });

  // ── Persist a step via POST /setup/step ──────────────────────────────────
  // R4 P0-1 fix: relative path; apiClient prepends `/api/v1`. The old
  // `/api/v1/setup/step` resolved to `/api/v1/api/v1/setup/step` (404).
  // R4 P1-7 fix: the POST /step response now mirrors the /state shape so
  // we can sync completedSteps from server truth in one round-trip.
  async function persistStep(stepId: StepId, data: Record<string, unknown>): Promise<boolean> {
    try {
      const res = await apiClient.post<{
        stepId: string;
        completed: boolean;
        currentStep: string;
        overallPercent: number;
        completedSteps?: string[];
        airgapRefused?: Array<{ kind: string; reason: string }>;
      }>('/setup/step', { stepId, data });
      // P2 review R4 / P2-BF-05 — an air-gapped install refuses external
      // data sources (GitHub, SharePoint, OneDrive, webhook) at the API.
      // The step still returns 200 so the optional step cannot strand the
      // installer, which means this response field is the ONLY signal the
      // operator gets. Without it they finish the wizard believing the
      // source exists and discover it missing at Library → Sources.
      if (Array.isArray(res?.airgapRefused) && res.airgapRefused.length > 0) {
        for (const refusal of res.airgapRefused) {
          toast.warning(
            `"${refusal.kind}" was not configured: ${refusal.reason}`,
          );
        }
      }
      // Prefer server-truth (handles P1-7 stale-currentStep): the GET /state
      // and POST /step now share the same composition logic.
      if (Array.isArray(res?.completedSteps)) {
        const next = new Set<StepId>();
        for (const id of res.completedSteps) next.add(id as StepId);
        setCompletedSteps(next);
      } else {
        setCompletedSteps((prev) => {
          const merged = new Set(prev);
          merged.add(stepId);
          return merged;
        });
      }
      return true;
    } catch (err) {
      toast.error(`Failed to save step "${stepId}": ${(err as Error).message ?? 'unknown error'}`);
      return false;
    }
  }

  // ── Step data builder ────────────────────────────────────────────────────
  function stepDataFor(stepId: StepId): Record<string, unknown> {
    switch (stepId) {
      case 'welcome':
        return { acceptedTerms: form.acceptedTerms, acceptedPrivacy: form.acceptedPrivacy };
      case 'sysadmin':
        // R4 P0-3 fix: server now actually creates the sysadmin user + system_admins
        // row when this step lands. Password is sent over TLS, hashed argon2id
        // server-side, and never logged. The audit row records only the email +
        // totpEnrollment flag, never the password or its hash.
        return {
          email: form.sysadminEmail,
          password: form.sysadminPassword,
          totpEnrollment: form.totpEnrollment,
        };
      case 'tenant':
        return {
          name: form.tenantName,
          slug: form.tenantSlug,
          orgCode: form.tenantOrgCode,
          industry: form.industry,
          mode: 'standalone',
        };
      case 'profile':
        return { profile: form.deploymentProfile };
      case 'model':
        return { primaryModel: form.primaryModel };
      case 'users':
        return {
          inviteEmails: form.inviteEmails
            .split(/[,\n]/)
            .map((e) => e.trim())
            .filter(Boolean),
        };
      case 'features':
        return {
          allow_web_search: form.allowWebSearch,
          allow_external_connections: form.allowExternalConnections,
        };
      case 'data_source':
        return { kind: form.dataSourceKind };
      case 'smoke_test':
        return { query: form.smokeQuery, ok: form.smokeOk };
      case 'complete':
        return { redirectTo: '/ask-argus' };
    }
  }

  async function handleNext() {
    if (!canAdvance()) return;
    setLoading(true);
    const ok = await persistStep(currentStep.id, stepDataFor(currentStep.id));
    setLoading(false);
    if (!ok) return;
    if (stepIndex < STEPS.length - 1) setStepIndex((i) => i + 1);
  }

  async function handleComplete() {
    setLoading(true);
    const ok = await persistStep('complete', stepDataFor('complete'));
    setLoading(false);
    if (!ok) return;
    toast.success('Setup complete — welcome to ArgusAI!');
    void navigate({ to: '/ask-argus' });
  }

  if (!stateKnown) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="setup-wizard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4" data-testid="setup-wizard">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-6">
          <Sparkles className="h-8 w-8 text-primary mx-auto mb-2" aria-hidden="true" />
          <h1 className="text-2xl font-bold">ArgusAI Setup</h1>
          <p className="text-muted-foreground" data-testid="setup-step-counter">
            Step {stepIndex + 1} of {STEPS.length} — {currentStep.label}
            {currentStep.optional && <span className="ml-2 text-xs text-muted-foreground">(optional)</span>}
          </p>
        </div>

        {/* Step indicator strip */}
        <div className="flex items-center justify-center gap-1 mb-6 flex-wrap">
          {STEPS.map((s, i) => {
            const done = completedSteps.has(s.id);
            const active = i === stepIndex;
            const Icon = s.icon;
            const canJump = done || i < stepIndex;
            return (
              <button
                key={s.id}
                type="button"
                title={`Step ${i + 1}: ${s.label}${s.optional ? ' (optional)' : ''}`}
                onClick={() => canJump && setStepIndex(i)}
                disabled={!canJump}
                data-testid={`setup-step-dot-${s.id}`}
                className={cn(
                  'h-9 w-9 rounded-full flex items-center justify-center text-xs font-medium transition-colors',
                  done ? 'bg-primary text-primary-foreground' :
                  active ? 'bg-primary/20 text-primary border-2 border-primary' :
                  'bg-muted text-muted-foreground',
                  canJump ? 'cursor-pointer hover:opacity-80' : 'cursor-default',
                )}
                aria-label={`Step ${i + 1}: ${s.label}`}
                aria-current={active ? 'step' : undefined}
              >
                {done ? <CheckCircle className="h-4 w-4" aria-hidden="true" /> : <Icon className="h-4 w-4" aria-hidden="true" />}
              </button>
            );
          })}
        </div>

        <Card>
          <CardContent className="p-6">
            {currentStep.id === 'welcome' && <StepWelcome form={form} update={update} />}
            {currentStep.id === 'sysadmin' && <StepSysadmin form={form} update={update} />}
            {currentStep.id === 'tenant' && <StepTenant form={form} update={update} />}
            {currentStep.id === 'profile' && <StepProfile form={form} update={update} gpuAvailable={gpuAvailable} />}
            {currentStep.id === 'model' && <StepModel form={form} update={update} models={models} gpuAvailable={gpuAvailable} />}
            {currentStep.id === 'users' && <StepUsers form={form} update={update} />}
            {currentStep.id === 'features' && <StepSettings form={form} update={update} />}
            {currentStep.id === 'data_source' && <StepDataSource form={form} update={update} />}
            {currentStep.id === 'smoke_test' && <StepSmokeTest form={form} update={update} />}
            {currentStep.id === 'complete' && <StepComplete form={form} services={services} />}
          </CardContent>
        </Card>

        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setStepIndex((s) => Math.max(0, s - 1))}
            disabled={stepIndex === 0 || loading}
            title="Previous step"
            data-testid="setup-back"
          >
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" /> Back
          </Button>
          {stepIndex < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!canAdvance() || loading}
              title="Save and continue"
              data-testid="setup-next"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" /> : null}
              {currentStep.optional ? 'Skip / Continue' : 'Next'}
              <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={loading}
              title="Finish setup and launch ArgusAI"
              data-testid="setup-complete"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" aria-hidden="true" />
              )}
              Complete Setup
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Step Components ────────────────────────────────────────────────────────

interface StepProps {
  form: SetupForm;
  update: <K extends keyof SetupForm>(k: K, v: SetupForm[K]) => void;
}

function StepWelcome({ form, update }: StepProps) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Welcome to ArgusAI</h2>
      <p className="text-sm text-muted-foreground">
        ArgusAI is an enterprise AI platform that runs entirely on your infrastructure. Before
        we provision your first tenant, please review the terms below.
      </p>
      <div className="rounded border bg-muted/30 p-4 text-xs space-y-2 max-h-48 overflow-y-auto" data-testid="setup-eula-text">
        <p className="font-semibold">End User License Agreement (summary)</p>
        <p>
          You acknowledge that ArgusAI runs on-premise inside your network boundary. No data is
          transmitted to external services without your explicit configuration. The platform is
          provided AS-IS with no warranty.
        </p>
        <p className="font-semibold mt-3">Privacy notice</p>
        <p>
          All conversations, documents, and audit logs remain on your infrastructure. ArgusAI
          does not phone home, telemetry-track, or otherwise exfiltrate data. Compliance modes
          (ITAR, CMMC, HIPAA, GDPR) can be enabled per-tenant via the admin dashboard.
        </p>
      </div>
      <div className="flex items-start gap-2 pt-2">
        <Checkbox
          id="terms"
          checked={form.acceptedTerms}
          onCheckedChange={(v) => update('acceptedTerms', !!v)}
          data-testid="setup-accept-terms"
        />
        <Label htmlFor="terms" className="cursor-pointer text-sm font-normal leading-tight">
          I accept the End User License Agreement.
        </Label>
      </div>
      <div className="flex items-start gap-2">
        <Checkbox
          id="privacy"
          checked={form.acceptedPrivacy}
          onCheckedChange={(v) => update('acceptedPrivacy', !!v)}
          data-testid="setup-accept-privacy"
        />
        <Label htmlFor="privacy" className="cursor-pointer text-sm font-normal leading-tight">
          I acknowledge the on-premise privacy notice.
        </Label>
      </div>
    </div>
  );
}

function StepSysadmin({ form, update }: StepProps) {
  const strength = passwordStrengthScore(form.sysadminPassword);
  const passwordsMatch =
    form.sysadminPasswordConfirm.length === 0 || form.sysadminPassword === form.sysadminPasswordConfirm;
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Create the first sysadmin account</h2>
      <p className="text-sm text-muted-foreground">
        This is the root account that provisions tenants and manages platform settings. We strongly
        recommend enrolling TOTP — sysadmin sessions require it.
      </p>
      <div>
        <Label htmlFor="sysEmail">Email</Label>
        <Input
          id="sysEmail"
          type="email"
          autoComplete="username"
          value={form.sysadminEmail}
          onChange={(e) => update('sysadminEmail', e.target.value)}
          placeholder="root@argus.local"
          data-testid="setup-sysadmin-email"
        />
      </div>
      <div>
        <Label htmlFor="sysPw">Password (min 12 chars, upper + lower + number + special)</Label>
        <Input
          id="sysPw"
          type="password"
          autoComplete="new-password"
          value={form.sysadminPassword}
          onChange={(e) => update('sysadminPassword', e.target.value)}
          data-testid="setup-sysadmin-password"
        />
        {form.sysadminPassword && <PasswordStrengthBar strength={strength} />}
      </div>
      <div>
        <Label htmlFor="sysPwConfirm">Confirm password</Label>
        <Input
          id="sysPwConfirm"
          type="password"
          autoComplete="new-password"
          value={form.sysadminPasswordConfirm}
          onChange={(e) => update('sysadminPasswordConfirm', e.target.value)}
          data-testid="setup-sysadmin-password-confirm"
        />
        {!passwordsMatch && <p className="text-xs text-destructive mt-1">Passwords do not match</p>}
      </div>
      <div className="flex items-start gap-2 pt-2">
        <Checkbox
          id="totp"
          checked={form.totpEnrollment}
          onCheckedChange={(v) => update('totpEnrollment', !!v)}
          data-testid="setup-totp"
        />
        <Label htmlFor="totp" className="cursor-pointer text-sm font-normal leading-tight">
          Enroll TOTP (recommended)
          <div className="text-xs text-muted-foreground mt-0.5">
            You will scan the QR code with an authenticator app after setup completes.
          </div>
        </Label>
      </div>
    </div>
  );
}

function StepTenant({ form, update }: StepProps) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">First tenant organization</h2>
      <p className="text-sm text-muted-foreground">
        Tenants are independently configured customer organizations. Standalone mode (this install)
        owns its own identity, users, and chat UI — Rule #2 forbids accepting IQ-issued JWTs here.
      </p>
      <div>
        <Label htmlFor="tName">Organization name</Label>
        <Input
          id="tName"
          value={form.tenantName}
          onChange={(e) => {
            update('tenantName', e.target.value);
            const auto = slugifyTenantName(e.target.value);
            if (auto.length >= 3) update('tenantSlug', auto);
            const code = orgCodeFromName(e.target.value);
            if (code.length >= 3) update('tenantOrgCode', code);
          }}
          placeholder="Acme Industrial"
          data-testid="setup-tenant-name"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="tSlug">Slug</Label>
          <Input
            id="tSlug"
            value={form.tenantSlug}
            onChange={(e) => update('tenantSlug', e.target.value.toLowerCase())}
            placeholder="acme-industrial"
            className="font-mono"
            data-testid="setup-tenant-slug"
          />
          <p className="text-xs text-muted-foreground mt-1">lowercase letters, digits, hyphens; 3–50 chars</p>
        </div>
        <div>
          <Label htmlFor="tOrgCode">Org code</Label>
          <Input
            id="tOrgCode"
            value={form.tenantOrgCode}
            onChange={(e) => update('tenantOrgCode', e.target.value.toUpperCase())}
            placeholder="ACME-IND"
            className="font-mono"
            data-testid="setup-tenant-orgcode"
          />
          <p className="text-xs text-muted-foreground mt-1">UPPERCASE; 3–30 chars</p>
        </div>
      </div>
      <div>
        <Label htmlFor="tIndustry">Industry (optional)</Label>
        <Input
          id="tIndustry"
          value={form.industry}
          onChange={(e) => update('industry', e.target.value)}
          placeholder="Manufacturing, Healthcare, Defense, …"
          data-testid="setup-tenant-industry"
        />
      </div>
      <div className="rounded border bg-muted/20 p-3 text-xs text-muted-foreground">
        Deployment mode is locked to <span className="font-mono">standalone</span>. To run in
        IQ-connected mode, abort this wizard and restart with <span className="font-mono">DEPLOYMENT_MODE=iq_connected</span>.
      </div>
    </div>
  );
}

interface ProfileStepProps extends StepProps {
  gpuAvailable: boolean;
}

function StepProfile({ form, update, gpuAvailable }: ProfileStepProps) {
  const options: Array<{ value: SetupForm['deploymentProfile']; label: string; desc: string; suggested?: boolean }> = [
    { value: 'cpu_only', label: 'CPU-Only',         desc: '5–10 users, Qwen 32B Q4, Ollama. ~$3–7K.',         suggested: !gpuAvailable },
    { value: 'standard_gpu', label: 'Standard GPU', desc: '20–50 users, A6000/L40S 48GB, vLLM Qwen 72B AWQ. ~$15–25K.', suggested: gpuAvailable },
    { value: 'high_perf',  label: 'High Performance', desc: '100–200+ users, 2× H100 80GB, 72B + Coder 32B. ~$80–200K.' },
    { value: 'edge',       label: 'Edge',           desc: '1–3 users, Jetson / NUC, Llama 3B or Qwen 8B. ~$800–3K.' },
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Pick a deployment profile</h2>
      <p className="text-sm text-muted-foreground">
        Per CLAUDE.md §15.2. {gpuAvailable ? 'GPU detected — Standard GPU is recommended.' : 'No GPU detected — CPU-Only is recommended.'}
      </p>
      <RadioGroup
        value={form.deploymentProfile}
        onValueChange={(v) => update('deploymentProfile', v as SetupForm['deploymentProfile'])}
      >
        {options.map((o) => (
          <div
            key={o.value}
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50',
              form.deploymentProfile === o.value && 'border-primary bg-primary/5',
            )}
            onClick={() => update('deploymentProfile', o.value)}
            data-testid={`setup-profile-${o.value}`}
          >
            <RadioGroupItem value={o.value} id={`profile-${o.value}`} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Label htmlFor={`profile-${o.value}`} className="font-medium cursor-pointer">{o.label}</Label>
                {o.suggested && <Badge variant="outline" className="text-xs">Suggested</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{o.desc}</p>
            </div>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}

interface ModelStepProps extends StepProps {
  models: Array<{ name: string; size: string }>;
  gpuAvailable: boolean;
}

function StepModel({ form, update, models, gpuAvailable }: ModelStepProps) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Pick a primary language model</h2>
      <p className="text-sm text-muted-foreground">
        {gpuAvailable
          ? 'These models are installed locally in Ollama / vLLM. You can swap later from sysadmin → Models.'
          : 'No GPU detected — pick a small CPU-friendly model. You can install more later via sysadmin → Models.'}
      </p>
      {models.length > 0 ? (
        <RadioGroup value={form.primaryModel} onValueChange={(v) => update('primaryModel', v)}>
          {models.map((m) => (
            <div
              key={m.name}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50',
                form.primaryModel === m.name && 'border-primary bg-primary/5',
              )}
              onClick={() => update('primaryModel', m.name)}
              data-testid={`setup-model-${m.name.replace(/[^a-zA-Z0-9]/g, '-')}`}
            >
              <RadioGroupItem value={m.name} id={`model-${m.name}`} />
              <Label htmlFor={`model-${m.name}`} className="font-mono cursor-pointer">{m.name}</Label>
              <Badge variant="outline">{m.size}</Badge>
            </div>
          ))}
        </RadioGroup>
      ) : (
        <div className="text-center py-8 text-muted-foreground" data-testid="setup-no-models">
          <AlertCircle className="h-8 w-8 mx-auto mb-2" aria-hidden="true" />
          <p>No models detected. Install one to continue:</p>
          <code className="text-xs bg-muted px-2 py-1 rounded inline-block mt-2">ollama pull qwen2.5:7b</code>
          <p className="text-xs mt-2">You can also continue without a model and add one later.</p>
        </div>
      )}
    </div>
  );
}

function StepUsers({ form, update }: StepProps) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Invite initial users (optional)</h2>
      <p className="text-sm text-muted-foreground">
        Add email addresses, comma- or newline-separated. They will receive a one-time invitation
        token. You can invite more users later from Users → Invitations.
      </p>
      <div>
        <Label htmlFor="invites">Email addresses</Label>
        <Textarea
          id="invites"
          rows={6}
          value={form.inviteEmails}
          onChange={(e) => update('inviteEmails', e.target.value)}
          placeholder={'alice@example.com\nbob@example.com'}
          className="font-mono"
          data-testid="setup-invite-emails"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Invitations are queued — they are sent after the sysadmin account is provisioned.
      </p>
    </div>
  );
}

function StepSettings({ form, update }: StepProps) {
  const TOGGLES: Array<{ key: keyof SetupForm; label: string; desc: string; required?: boolean }> = [
    { key: 'allowWebSearch', label: 'Allow web search', desc: 'Let people turn on live web search per turn in the chat composer. Results are marked as an unverified Web source.' },
    { key: 'allowExternalConnections', label: 'Allow external connections', desc: 'Let this tenant connect out to outside systems — connectors and MCP servers. Off means fully inward-facing.' },
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Tenant settings</h2>
      <p className="text-sm text-muted-foreground">
        Every rail and every capability is on. These two settings decide whether this
        tenant may reach outside; the tenant admin can change either later in
        Admin → Settings → General.
      </p>
      <div className="space-y-2">
        {TOGGLES.map((t) => (
          <div key={t.key} className="flex items-start gap-3 p-3 rounded-lg border">
            <Checkbox
              id={`feat-${t.key}`}
              checked={!!form[t.key]}
              onCheckedChange={(v) => update(t.key, !!v as SetupForm[typeof t.key])}
              disabled={t.required}
              data-testid={`setup-feature-${t.key}`}
            />
            <Label htmlFor={`feat-${t.key}`} className="cursor-pointer flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{t.label}</span>
                {t.required && <Badge variant="outline" className="text-xs">Required</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 font-normal">{t.desc}</p>
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepDataSource({ form, update }: StepProps) {
  const SOURCES: Array<{ value: SetupForm['dataSourceKind']; label: string; desc: string }> = [
    { value: 'skip', label: 'Skip for now', desc: 'Connect a data source later from Library → Sources.' },
    { value: 'sharepoint', label: 'SharePoint', desc: 'Connect SharePoint sites for document ingestion.' },
    { value: 'gdrive', label: 'Google Drive', desc: 'Connect a Drive folder.' },
    { value: 's3', label: 'S3 / object storage', desc: 'AWS S3, MinIO, or any S3-compatible bucket.' },
    { value: 'local', label: 'Local upload', desc: 'Just drag-and-drop PDFs after setup.' },
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Connect a data source (optional)</h2>
      <p className="text-sm text-muted-foreground">
        ArgusAI ingests documents for RAG retrieval. Pick a source to configure, or skip and add later.
      </p>
      <RadioGroup
        value={form.dataSourceKind}
        onValueChange={(v) => update('dataSourceKind', v as SetupForm['dataSourceKind'])}
      >
        {SOURCES.map((s) => (
          <div
            key={s.value}
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50',
              form.dataSourceKind === s.value && 'border-primary bg-primary/5',
            )}
            onClick={() => update('dataSourceKind', s.value)}
            data-testid={`setup-data-source-${s.value}`}
          >
            <RadioGroupItem value={s.value} id={`src-${s.value}`} />
            <div className="flex-1">
              <Label htmlFor={`src-${s.value}`} className="font-medium cursor-pointer">{s.label}</Label>
              <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </RadioGroup>
      {form.dataSourceKind !== 'skip' && (
        <p className="text-xs text-muted-foreground">
          Connector configuration will open in a new tab after setup completes.
        </p>
      )}
    </div>
  );
}

function StepSmokeTest({ form, update }: StepProps) {
  const [running, setRunning] = useState(false);

  async function runSmoke() {
    setRunning(true);
    try {
      // R4 P0-1 + P1-3 fix: was posting to /api/v1/setup/setup/detect — a path
      // that didn't exist (GET-only) AND was double-prefixed. The new
      // /setup/smoke-test endpoint actually runs a tiny chat through the
      // default brand and returns latency + an honest pass/fail.
      const res = await apiClient.post<{ ok: boolean; message: string; latencyMs: number }>(
        '/setup/smoke-test',
        { query: form.smokeQuery },
      );
      const ok = !!res?.ok;
      update('smokeOk', ok);
      update(
        'smokeResult',
        ok
          ? `${res.message} (${res.latencyMs} ms)`
          : (res?.message ?? 'Smoke test failed.'),
      );
      if (ok) toast.success('Smoke test passed.');
      else toast.error(`Smoke test failed: ${res?.message ?? 'unknown error'}`);
    } catch (err) {
      update('smokeOk', false);
      update('smokeResult', `Smoke test failed: ${(err as Error).message ?? 'unknown error'}`);
      toast.error('Smoke test failed.');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Run a smoke test</h2>
      <p className="text-sm text-muted-foreground">
        We&apos;ll send a tiny request through the platform to confirm everything is wired up.
        You can also skip ahead — the operator can always run more tests from Health.
      </p>
      <div>
        <Label htmlFor="smokeQ">Smoke query</Label>
        <Textarea
          id="smokeQ"
          rows={3}
          value={form.smokeQuery}
          onChange={(e) => update('smokeQuery', e.target.value)}
          data-testid="setup-smoke-query"
        />
      </div>
      <Button
        variant="outline"
        onClick={runSmoke}
        disabled={running}
        title="Send the smoke-test request"
        data-testid="setup-smoke-run"
      >
        {running ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
        ) : (
          <Send className="h-4 w-4 mr-2" aria-hidden="true" />
        )}
        Run smoke test
      </Button>
      {form.smokeResult && (
        <div
          className={cn(
            'rounded border p-3 text-sm',
            form.smokeOk ? 'border-green-500/40 bg-green-500/5' : 'border-destructive/40 bg-destructive/5',
          )}
          data-testid="setup-smoke-result"
        >
          {form.smokeResult}
        </div>
      )}
      <div className="flex items-center gap-2 pt-2">
        <Checkbox
          id="smokeOverride"
          checked={form.smokeOk}
          onCheckedChange={(v) => update('smokeOk', !!v)}
          data-testid="setup-smoke-override"
        />
        <Label htmlFor="smokeOverride" className="text-sm font-normal cursor-pointer">
          Mark smoke test as complete (overrides automatic check)
        </Label>
      </div>
    </div>
  );
}

function StepComplete({ form, services }: { form: SetupForm; services: ServiceStatus[] }) {
  return (
    <div className="space-y-4 text-center" data-testid="setup-step-complete">
      <CheckCircle className="h-12 w-12 text-green-500 mx-auto" aria-hidden="true" />
      <h2 className="text-lg font-semibold">Ready to launch</h2>
      <p className="text-sm text-muted-foreground">
        ArgusAI is configured. Click <span className="font-medium">Complete Setup</span> to finish
        and open Ask Argus.
      </p>
      <div className="text-left space-y-1 text-sm bg-muted/30 rounded p-4">
        <SummaryRow label="Mode" value="standalone" mono />
        <SummaryRow label="Tenant" value={form.tenantName} />
        <SummaryRow label="Slug" value={form.tenantSlug} mono />
        <SummaryRow label="Sysadmin" value={form.sysadminEmail} />
        <SummaryRow label="Profile" value={form.deploymentProfile} mono />
        <SummaryRow label="Model" value={form.primaryModel || '(none configured)'} mono />
        <SummaryRow
          label="Services"
          value={`${services.filter((s) => s.status === 'healthy').length}/${services.length} healthy`}
        />
      </div>
    </div>
  );
}

function SummaryRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('font-medium', mono && 'font-mono text-xs')}>{value}</span>
    </div>
  );
}

// ─── Password Strength Bar ──────────────────────────────────────────────────
// Scoring lives in SetupWizard.helpers.passwordStrengthScore so both the
// wizard and the unit tests pin the same algorithm.

function PasswordStrengthBar({ strength }: { strength: number }) {
  const colors = ['bg-red-500', 'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-400', 'bg-green-600'];
  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={cn('h-1.5 flex-1 rounded-full', i < strength ? colors[strength] : 'bg-muted')} />
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-1">{labels[strength]}</p>
    </div>
  );
}
